<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import DyteAPI from 'dashboard/api/integrations/dyte';
import {
  leaveRealtimeKitMeeting,
  mountRealtimeKitMeeting,
} from 'shared/helpers/RealtimeKitMeetingHelper';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import { useMessageContext } from '../provider.js';
import BaseAttachmentBubble from './BaseAttachment.vue';

const { content, sender, id } = useMessageContext();

const { t, locale } = useI18n();

const isLoading = ref(false);
const isInCall = ref(false);
const meetingContainer = ref(null);
const meetingClient = ref(null);
let joinGeneration = 0;

const cleanupMeeting = async () => {
  joinGeneration += 1;
  const meeting = meetingClient.value;
  meetingClient.value = null;
  await leaveRealtimeKitMeeting(meeting);
  meetingContainer.value?.replaceChildren();
  isInCall.value = false;
};

const joinTheCall = async () => {
  if (isLoading.value || isInCall.value) return;

  const generation = joinGeneration + 1;
  joinGeneration = generation;
  isLoading.value = true;

  try {
    const { data: { token } = {} } = await DyteAPI.addParticipantToMeeting(
      id.value
    );
    if (generation !== joinGeneration) return;

    isInCall.value = true;
    await nextTick();
    if (generation !== joinGeneration || !meetingContainer.value) return;

    const meeting = await mountRealtimeKitMeeting(
      meetingContainer.value,
      token,
      { locale: locale.value }
    );
    if (generation !== joinGeneration) {
      await leaveRealtimeKitMeeting(meeting);
      return;
    }
    meetingClient.value = meeting;
  } catch (err) {
    if (generation === joinGeneration) {
      await cleanupMeeting();
      useAlert(t('INTEGRATION_SETTINGS.DYTE.JOIN_ERROR'));
    }
  } finally {
    if (generation === joinGeneration) {
      isLoading.value = false;
    }
  }
};

const leaveTheRoom = async () => {
  await cleanupMeeting();
};

const action = computed(() => ({
  label: t('INTEGRATION_SETTINGS.DYTE.CLICK_HERE_TO_JOIN'),
  onClick: joinTheCall,
}));

onBeforeUnmount(() => {
  cleanupMeeting();
});
</script>

<template>
  <BaseAttachmentBubble
    icon="i-ph-video-camera-fill"
    icon-bg-color="bg-[#2781F6]"
    sender-translation-key="CONVERSATION.SHARED_ATTACHMENT.MEETING"
    :action="action"
  >
    <div v-if="!sender" class="text-sm truncate text-n-slate-12">
      <!-- Added as a fallback, where the sender is not available (Deleted) -->
      <!-- Will show the content, if senderName in BaseAttachment.vue is empty -->
      {{ content }}
    </div>
    <div v-if="isInCall" class="video-call--container">
      <div ref="meetingContainer" class="video-call--meeting" />
      <button
        class="px-4 py-2 text-sm rounded-lg bg-n-solid-3 mt-3"
        @click="leaveTheRoom"
      >
        {{ $t('INTEGRATION_SETTINGS.DYTE.LEAVE_THE_ROOM') }}
      </button>
    </div>
  </BaseAttachmentBubble>
</template>

<style lang="scss">
.video-call--container {
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  padding: 0.25rem;
  @apply bg-n-background;

  .video-call--meeting {
    width: 100%;
    height: 100%;
  }

  button {
    position: absolute;
    top: 0.25rem;
    right: 10rem;
  }
}
</style>
